<template>
  <div class="cal">
    <div class="page-head">
      <div class="acts">
        <el-button :loading="loading" @click="reload">刷新</el-button>
        <el-button :loading="reviewing" type="warning" plain @click="doReview">跑复盘</el-button>
        <el-button :loading="attributing" type="success" plain @click="doAttribution">标题归因回写</el-button>
        <el-button :loading="scanning" @click="scan">扫描建议</el-button>
        <el-button type="primary" plain :loading="batching" @click="batchEnqueue">批量入队已批准</el-button>
      </div>
    </div>

    <el-row :gutter="12" class="filters">
      <el-col :span="6">
        <el-select v-model="platform" style="width:100%" @change="reload">
          <el-option label="小红书" value="xiaohongshu" />
          <el-option label="抖音" value="douyin" />
        </el-select>
      </el-col>
      <el-col :span="10">
        <el-input v-model="accountId" placeholder="账号 ID" @keyup.enter="reload" />
      </el-col>
      <el-col :span="8">
        <el-select v-model="status" clearable placeholder="状态" style="width:100%" @change="reload">
          <el-option label="全部" value="" />
          <el-option label="草稿" value="draft" />
          <el-option label="就绪" value="ready" />
          <el-option label="已批准" value="approved" />
          <el-option label="已入队" value="queued" />
          <el-option label="已发布" value="published" />
        </el-select>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :span="12">
        <el-card shadow="never">
          <template #header>发布日历</template>
          <div v-if="!items.length" class="empty">暂无排期</div>
          <div v-for="it in items" :key="it.id" class="item">
            <div class="row">
              <strong>{{ it.title }}</strong>
              <el-tag size="small">{{ it.status }}</el-tag>
            </div>
            <div class="muted">#{{ it.id }} · {{ it.scheduled_at || '未排期' }} · {{ (it.target_platforms||[]).join('/') }}</div>
            <div class="row" style="margin-top:8px; gap:6px; flex-wrap:wrap;">
              <el-button size="small" @click="setStatus(it, 'ready')">就绪</el-button>
              <el-button size="small" type="success" @click="setStatus(it, 'approved')">批准</el-button>
              <el-button size="small" type="primary" :loading="enqueueingId === it.id" @click="enqueueItem(it, true)">
                确认入队(草稿)
              </el-button>
              <el-button size="small" :loading="enqueueingId === it.id" @click="enqueueItem(it, true, true)">
                强制入队
              </el-button>
              <el-button size="small" type="danger" plain @click="setStatus(it, 'rejected')">驳回</el-button>
            </div>
            <div v-if="it.article_id || it.publish_job_id" class="muted">
              文章 #{{ it.article_id || '—' }} · Job #{{ it.publish_job_id || '—' }}
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card shadow="never">
          <template #header>待确认建议</template>
          <div v-if="!proposals.length" class="empty">暂无主动建议</div>
          <div v-for="p in proposals" :key="p.id" class="item">
            <div class="row">
              <strong>{{ p.title }}</strong>
              <el-tag size="small" type="warning">{{ p.proposal_type }}</el-tag>
            </div>
            <p class="muted">{{ p.reason }}</p>
            <div class="row" style="gap:6px;">
              <el-button size="small" type="primary" @click="decide(p, true)">接受</el-button>
              <el-button size="small" @click="decide(p, false)">拒绝</el-button>
            </div>
          </div>
        </el-card>

        <el-card shadow="never" style="margin-top:12px">
          <template #header>本周计划</template>
          <div v-if="!weekly" class="empty">暂无周计划</div>
          <div v-else>
            <div class="muted">周起始 {{ weekly.week_start }}</div>
            <div v-for="(d, i) in (weekly.plan?.days || [])" :key="i" class="day">
              <strong>{{ d.weekday }}</strong> · {{ d.focus }}
              <ul>
                <li v-for="(t, j) in (d.tasks || [])" :key="j">{{ t.name }} — {{ t.detail }}</li>
              </ul>
            </div>
            <el-button size="small" :loading="planning" @click="makeWeekly">重新生成周计划</el-button>
          </div>
        </el-card>

        <el-card shadow="never" style="margin-top:12px">
          <template #header>品牌工作区</template>
          <div class="row" style="gap:8px; margin-bottom:8px;">
            <el-input v-model="newBrandName" placeholder="新品牌名称" size="small" />
            <el-button size="small" type="primary" :loading="creatingBrand" @click="createBrand">创建              创建
            </el-button>
          </div>
          <div v-if="!workspaces.length" class="empty">暂无品牌组，可创建后把多账号归入同一品牌</div>
          <div v-for="w in workspaces" :key="w.id" class="item" :class="{ active: currentWorkspace?.id === w.id }" @click="selectWorkspace(w)">
            <div class="row">
              <strong>{{ w.name }}</strong>
              <el-tag size="small">{{ (w.members || []).length }} 账号</el-tag>
            </div>
            <div class="muted">授权默认 {{ w.defaults?.authorization_mode || 'suggest' }}</div>
            <el-button
              v-if="accountId"
              size="small"
              style="margin-top:6px"
              @click.stop="addCurrentToBrand(w)"
            >把当前账号加入</el-button>
          </div>
        </el-card>

        <el-card v-if="reviewResult" shadow="never" style="margin-top:12px">
          <template #header>最近复盘</template>
          <pre class="body">{{ pretty(reviewResult) }}</pre>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { contentOpsApi } from '../api'

export default {
  name: 'ContentOpsCalendar',
  setup() {
    const platform = ref('xiaohongshu')
    const accountId = ref('')
    const status = ref('')
    const items = ref([])
    const proposals = ref([])
    const weekly = ref(null)
    const reviewResult = ref(null)
    const loading = ref(false)
    const scanning = ref(false)
    const reviewing = ref(false)
    const attributing = ref(false)
    const planning = ref(false)
    const enqueueingId = ref(null)
    const batching = ref(false)
    const workspaces = ref([])
    const currentWorkspace = ref(null)
    const newBrandName = ref('')
    const creatingBrand = ref(false)

    const pretty = (o) => JSON.stringify(o, null, 2)

    const reload = async () => {
      loading.value = true
      try {
        const params = { platform: platform.value }
        if (accountId.value) params.account_id = accountId.value
        if (status.value) params.status = status.value
        const [cal, prop, weeks, brands] = await Promise.all([
          contentOpsApi.listCalendar(params),
          contentOpsApi.listProposals({ ...params, status: 'pending' }),
          contentOpsApi.listWeeklyPlans(params),
          contentOpsApi.listWorkspaces(),
        ])
        items.value = cal.items || []
        proposals.value = prop.items || []
        weekly.value = (weeks.items || [])[0] || null
        workspaces.value = brands.items || []
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    const enqueueItem = async (it, asDraft = true, force = false) => {
      enqueueingId.value = it.id
      try {
        const res = await contentOpsApi.enqueueCalendar(it.id, {
          confirm: true,
          draft_only: asDraft,
          force,
        })
        ElMessage.success(res.message || '已入队')
        reload()
      } catch (e) {
        ElMessage.error(e.message || '入队失败')
      } finally {
        enqueueingId.value = null
      }
    }

    const batchEnqueue = async () => {
      batching.value = true
      try {
        const res = await contentOpsApi.enqueueApproved({
          platform: platform.value,
          account_id: accountId.value || undefined,
          confirm: true,
          draft_only: true,
        })
        ElMessage.success(`已处理 ${res.total || 0} 条已批准项`)
        reload()
      } catch (e) {
        ElMessage.error(e.message || '批量入队失败')
      } finally {
        batching.value = false
      }
    }

    const createBrand = async () => {
      if (!newBrandName.value.trim()) return
      creatingBrand.value = true
      try {
        const w = await contentOpsApi.createWorkspace({ name: newBrandName.value.trim() })
        newBrandName.value = ''
        currentWorkspace.value = w
        ElMessage.success('品牌工作区已创建')
        reload()
      } catch (e) {
        ElMessage.error(e.message || '创建失败')
      } finally {
        creatingBrand.value = false
      }
    }

    const selectWorkspace = (w) => {
      currentWorkspace.value = w
      if (w.defaults?.authorization_mode) {
        ElMessage.info(`已切换工作区：${w.name}`)
      }
    }

    const addCurrentToBrand = async (w) => {
      if (!accountId.value) {
        ElMessage.warning('请先填写账号 ID')
        return
      }
      try {
        await contentOpsApi.addWorkspaceMember(w.id, {
          platform: platform.value,
          account_id: accountId.value,
          role: 'editor',
        })
        ElMessage.success('已加入品牌组')
        reload()
      } catch (e) {
        ElMessage.error(e.message || '加入失败')
      }
    }

    const setStatus = async (it, st) => {
      try {
        await contentOpsApi.patchCalendar(it.id, { status: st })
        ElMessage.success('已更新')
        reload()
      } catch (e) {
        ElMessage.error(e.message || '失败')
      }
    }

    const decide = async (p, accept) => {
      try {
        const res = await contentOpsApi.decideProposal(p.id, { accept })
        ElMessage.success(res.note || (accept ? '已接受' : '已拒绝'))
        reload()
      } catch (e) {
        ElMessage.error(e.message || '失败')
      }
    }

    const scan = async () => {
      scanning.value = true
      try {
        await contentOpsApi.scanProposals()
        await reload()
        ElMessage.success('已扫描')
      } catch (e) {
        ElMessage.error(e.message || '失败')
      } finally {
        scanning.value = false
      }
    }

    const doReview = async () => {
      if (!accountId.value) { ElMessage.warning('请填写账号'); return }
      reviewing.value = true
      try {
        reviewResult.value = await contentOpsApi.review({
          platform: platform.value,
          account_id: accountId.value,
          days: 14,
        })
        ElMessage.success('复盘完成')
      } catch (e) {
        ElMessage.error(e.message || '复盘失败')
      } finally {
        reviewing.value = false
      }
    }

    const doAttribution = async () => {
      if (!accountId.value) { ElMessage.warning('请填写账号'); return }
      attributing.value = true
      try {
        reviewResult.value = await contentOpsApi.attributionRun({
          platform: platform.value,
          account_id: accountId.value,
          days: 14,
        })
        ElMessage.success('标题归因已回写策略/记忆')
      } catch (e) {
        ElMessage.error(e.message || '归因失败')
      } finally {
        attributing.value = false
      }
    }

    const makeWeekly = async () => {
      if (!accountId.value) { ElMessage.warning('请填写账号'); return }
      planning.value = true
      try {
        weekly.value = await contentOpsApi.weeklyPlan({
          platform: platform.value,
          account_id: accountId.value,
          goal_text: '持续内容增长',
        })
        ElMessage.success('周计划已生成')
      } catch (e) {
        ElMessage.error(e.message || '失败')
      } finally {
        planning.value = false
      }
    }

    onMounted(reload)
    return {
      platform, accountId, status, items, proposals, weekly, reviewResult,
      loading, scanning, reviewing, attributing, planning, enqueueingId, batching,
      workspaces, currentWorkspace, newBrandName, creatingBrand,
      pretty, reload, setStatus, decide, scan, doReview, doAttribution, makeWeekly,
      enqueueItem, batchEnqueue, createBrand, selectWorkspace, addCurrentToBrand,
    }
  },
}
</script>

<style scoped>
.page-head { display:flex; justify-content:flex-end; margin-bottom:12px; gap:8px; }
.page-title { margin:0 0 4px; font-size:18px; font-weight:750; }
.page-desc { margin:0; color:var(--mp-text-muted); font-size:12px; }
.acts { display:flex; gap:8px; flex-wrap:wrap; }
.filters { margin-bottom:12px; }
.empty { color:var(--mp-text-muted); font-size:12px; padding:12px 0; }
.item { border:1px solid var(--mp-border); border-radius:8px; padding:10px; margin-bottom:8px; }
.item.active { border-color: var(--el-color-primary); }
.row { display:flex; justify-content:space-between; align-items:center; }
.muted { color:var(--mp-text-muted); font-size:12px; margin:4px 0; }
.day { margin:10px 0; font-size:12px; }
.day ul { margin:4px 0 0 16px; padding:0; color:var(--mp-text-muted); }
.body { max-height:240px; overflow:auto; font-size:11px; background:#0f1218; color:#d7dde8; padding:10px; border-radius:8px; }
</style>
